import CustomRegistrationService from '../components/sign-up/custom-registration.service';
import FeatureToggleResolver from '../feature-toggles/feature-toggle-resolver';
import { UnifiedEmailVerificationExpiredComponent } from '../ng-app/unified-registration/expired/expired.component';
import { UnifiedEmailVerificationAlreadyVerifiedComponent } from '../ng-app/unified-registration/already-verified/already-verified.component';
import { UnifiedRegistrationErrorComponent } from '../ng-app/unified-registration/error/error.component';
import { UnifiedSetPasswordComponent } from '../ng-app/unified-registration/set-password/set-password.component';
import { UnifiedRegistrationLayoutComponent } from '../ng-app/unified-registration/layout/layout.component';
import { UnifiedRegistrationFormComponent } from '../ng-app/unified-registration/form/form.component';
import { UnifiedRegistrationSuccessComponent } from '../ng-app/unified-registration/success/success.component';
import { CustomRegistrationLandingComponent } from '../ng-app/unified-registration/custom-registration-landing/custom-registration-landing.component';
import { UnifiedAlreadySignedInComponent } from '../ng-app/unified-registration/already-signed-in/already-signed-in.component';

const customSignupUnifiedState = {
  url: '/c-signup/:id?utm_source&utm_medium&utm_campaign&utm_content&utm_term',
  parent: 'signup-layout',
  component: CustomRegistrationLandingComponent,
  resolve: {
    title: [() => 'Join the uTest Community - Custom Registration'],
  },
};

const signupLayoutState = {
  abstract: true,
  component: UnifiedRegistrationLayoutComponent,
  resolve: {
    features: FeatureToggleResolver,
    forceHideLoginButton: () => false,
  },
};

const signupFormState = {
  url: '/signup?utm_source&utm_medium&utm_campaign&utm_content&customRegistrationId?first-name&last-name&email&month&day&year&country&terms&newsletter&autoSubmit',
  parent: 'signup-layout',
  component: UnifiedRegistrationFormComponent,
  redirectTo: (trans) => trans.injector().getAsync('UserService').then((UserService) => UserService.isPlatformLoggedIn().then((result) => {
    if (!result || result.data !== true) {
      return false;
    }

    // If user has a custom registration ID, redirect to the embedded form
    const { customRegistrationId } = trans.params();
    if (customRegistrationId) {
      console.log('User is already authenticated, redirecting to custom registration embedded form');

      const CustomRegistrationService = trans.injector().get('CustomRegistrationService');
      const $window = trans.injector().get('$window');

      // Fetch custom registration and build embedded form URL
      return CustomRegistrationService.find(customRegistrationId).then((response) => {
        const customRegistration = response.data;

        // Extract UTM params
        const utmParams: any = {};
        const params = trans.params();
        if (params.utm_source) utmParams.utm_source = params.utm_source;
        if (params.utm_medium) utmParams.utm_medium = params.utm_medium;
        if (params.utm_campaign) utmParams.utm_campaign = params.utm_campaign;
        if (params.utm_content) utmParams.utm_content = params.utm_content;
        if (params.utm_term) utmParams.utm_term = params.utm_term;

        return UserService.me().then((userResult) => {
          // Build embedded form URL
          const embeddedFormUrl = CustomRegistrationService.buildEmbeddedFormUrl(
            customRegistration.embedded_form,
            userResult.data,
            utmParams,
          );

          // Redirect to embedded form
          $window.location.href = embeddedFormUrl;
          // Return a promise that never resolves to prevent state transition
          return new Promise(() => {});
        });
      }).catch((error) => {
        console.error('Failed to fetch custom registration:', error);
        return { state: 'home' };
      });
    }

    console.log('User is already authenticated, redirecting to home');
    return { state: 'home' };
  }).catch(() => false)),
  resolve: {
    title: [() => 'Join the uTest Community - Sign Up'],
    meta: ['title', 'Meta', function (title, Meta) {
      Meta.updateTag({ name: 'og:title', property: 'og:title', content: title });
      Meta.updateTag({ name: 'description', property: 'description', content: 'Join the uTest community and start testing apps and websites. Sign up today to become part of the world\'s largest testing community.' });
    }],
  },
};

const signupSuccessState = {
  url: '/signup/success?customRegistrationId',
  parent: 'signup-layout',
  component: UnifiedRegistrationSuccessComponent,
  resolve: {
    title: [() => 'Confirm Your Account - uTest'],
  },
};

const signupErrorState = {
  url: '/signup/error',
  parent: 'signup-layout',
  component: UnifiedRegistrationErrorComponent,
  resolve: {
    title: [() => 'Registration Error - uTest'],
  },
};

const signUpAlreadySignedInState = {
  url: '/signup/auth-error',
  parent: 'signup-layout',
  component: UnifiedAlreadySignedInComponent,
  resolve: {
    title: [() => 'Registration Error - uTest'],
  },
};

const setPasswordLayoutState = {
  abstract: true,
  component: UnifiedRegistrationLayoutComponent,
  resolve: {
    features: FeatureToggleResolver,
    forceHideLoginButton: () => true,
  },
};

const setPasswordFormState = {
  url: '/set-password?code&userCode&rt',
  parent: 'setpassword-layout',
  component: UnifiedSetPasswordComponent,
  resolve: {
    title: [() => 'Set Your Password - uTest'],
  },
};

const setPasswordExpiredState = {
  url: '/set-password/expired',
  parent: 'setpassword-layout',
  component: UnifiedEmailVerificationExpiredComponent,
  params: {
    emailCode: null,
  },
  resolve: {
    title: [() => 'Password Link Expired - uTest'],
  },
};

const setPasswordAlreadyVerifiedState = {
  url: '/set-password/already-verified',
  parent: 'setpassword-layout',
  component: UnifiedEmailVerificationAlreadyVerifiedComponent,
  resolve: {
    title: [() => 'Email Already Verified - uTest'],
  },
};

const setPasswordErrorState = {
  url: '/set-password/error',
  parent: 'setpassword-layout',
  component: UnifiedRegistrationErrorComponent,
  resolve: {
    title: [() => 'Password Setting Error - uTest'],
  },
};

const SignUpRouter = {
  route: (module) => {
    module
      .service('CustomRegistrationService', CustomRegistrationService);

    module.config(['$stateProvider', ($stateProvider) => {
      $stateProvider
        .state('referral', {
          controller: ['$location', '$transition$', '$timeout', function ($location, $transition$, $timeout) {
            const redirectTo = `/signup?utm_source=profile-referral&utm_medium=referral&utm_campaign=leads&utm_content=id-${$transition$.params().testerId}`;
            $timeout(() => $location.url(redirectTo));
          }],
          url: '/ref{testerId:[0-9]+}',
        })
        .state('signup-layout', signupLayoutState)
        .state('signup', signupFormState)
        .state('signup-success', signupSuccessState)
        .state('signup-error', signupErrorState)
        .state('signup-already-signed-in', signUpAlreadySignedInState)
        .state('customsignup-unified', customSignupUnifiedState)
        .state('setpassword-layout', setPasswordLayoutState)
        .state('setpassword-form', setPasswordFormState)
        .state('setpassword-expired', setPasswordExpiredState)
        .state('setpassword-already-verified', setPasswordAlreadyVerifiedState)
        .state('setpassword-error', setPasswordErrorState);
    }]);
  },
};

export default SignUpRouter;
